import { PersonStatus_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import { useTranslation } from '@frontend/i18n';
import { titleCase } from '@frontend/string';
import { theme } from '@frontend/theme';
import { Avatar, Button, Heading, Modal, ModalControlModalProps, Text, Truncated } from '@frontend/design-system';

type ContactDetails = {
  firstName: string;
  lastName: string;
  status: PersonStatus_Enum;
  age: number;
  gender: string;
  avatarUrl?: string;
};

interface Props extends ModalControlModalProps {
  contactDetails: ContactDetails;
  onBookAppointment: () => void;
}

export const BookAppointmentModal = ({ contactDetails, onBookAppointment, ...modalProps }: Props) => {
  const { t } = useTranslation('schedule-onboarding');
  const { avatarUrl, firstName, lastName, age, gender, status } = contactDetails;

  return (
    <Modal {...modalProps} maxWidth={400} disableCloseOnEscape disableCloseOnOverlayClick>
      <Modal.Header>
        <Heading level={2} css={{ lineHeight: '28px', marginBottom: theme.spacing(1) }}>
          {t('Perfect! Your scheduling is ready')}
        </Heading>
        <Text weight='regular'>
          {t("Let's book your first appointment to see how it works. A test contact has been set up for your practice")}
        </Text>
      </Modal.Header>
      <Modal.Body>
        <div css={containerStyles}>
          <div>
            <Avatar
              size='xxl'
              src={avatarUrl}
              alt={`${firstName} ${lastName}`}
              firstName={firstName}
              lastName={lastName}
            />
          </div>
          <div>
            <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), maxWidth: 220 }}>
              <Heading level={2}>{firstName}</Heading>
              <Truncated as='h2' css={{ fontWeight: theme.font.weight.regular }}>
                {lastName}
              </Truncated>
            </div>
            <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
              <Text size='small' color='subdued'>
                {titleCase(PersonStatus_Enum[status] || PersonStatus_Enum.UNSPECIFIED)}
              </Text>
              <Text size='small' color='subdued'>
                {age} {t('Yrs')}
              </Text>
              <Text size='small' color='subdued'>
                {titleCase(gender)}
              </Text>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onBookAppointment}>{t('Book Appointment')}</Button>
      </Modal.Footer>
    </Modal>
  );
};

const containerStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  padding: theme.spacing(2),
  margin: `${theme.spacing(1)} auto`,
  border: `1px solid ${theme.colors.neutral20}`,
  borderRadius: theme.borderRadius.large,
  maxWidth: '100%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
};
